import React from 'react';
import { motion } from 'framer-motion';
import {
    Camera, MessageSquare, Trophy, Heart,
    Download, Share2, Sparkles, Play, Star,
    Award, Image as ImageIcon, ChevronRight
} from 'lucide-react';

const EventRecapView = ({ summary, event, onDownload, onShare }) => {
    if (!summary) return null;

    // FALLBACK for Legacy Data (Old Summaries without stats)
    if (!summary.stats) {
        return (
            <div className="bg-[#fdfbf7] min-h-screen pb-24 flex flex-col items-center pt-20 px-6 text-center">
                <h1 className="text-3xl font-black text-slate-900 mb-2">Dein Rückblick</h1>
                <p className="text-slate-500 text-sm font-medium mb-8">Erstellt am {new Date(summary.generated_at || Date.now()).toLocaleDateString()}</p>

                <div className="w-full max-w-sm aspect-[3/4] bg-white rounded-[2rem] shadow-xl overflow-hidden mb-8 border border-slate-100">
                    {summary.image && <img src={summary.image} alt="Recap" className="w-full h-full object-cover" />}
                </div>

                <div className="max-w-md bg-amber-50 p-6 rounded-3xl border border-amber-100 italic font-serif text-slate-700 text-lg leading-relaxed shadow-sm">
                    {summary.text}
                </div>

                <div className="fixed bottom-6 left-6 right-6 z-50 flex gap-3">
                    <button onClick={onShare} className="flex-1 bg-slate-900 text-white rounded-2xl py-5 font-black uppercase tracking-widest text-xs shadow-xl flex items-center justify-center gap-2">
                        <Share2 className="w-4 h-4" /> Teilen
                    </button>
                    <button onClick={onDownload} className="w-16 h-16 bg-white text-slate-900 rounded-2xl border border-slate-200 shadow-xl flex items-center justify-center">
                        <Download className="w-6 h-6" />
                    </button>
                </div>
            </div>
        );
    }

    const { stats, text, video_preview } = summary;

    return (
        <div className="bg-[#fdfbf7] min-h-screen pb-24">
            {/* Top Bar / Header */}
            <div className="p-6 pt-10 text-center">
                <span className="text-[10px] font-black tracking-[0.2em] text-amber-500 uppercase mb-2 block">
                    Unvergesslich
                </span>
                <h1 className="text-4xl font-black text-slate-900 mb-2">
                    Das war euer Tag
                </h1>
                <p className="text-slate-500 text-sm font-medium">
                    Ein kleiner Rückblick in Zahlen und Momenten.
                </p>
            </div>

            <div className="px-6 space-y-4">
                {/* Main Stat: Photos */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100 flex items-center justify-between"
                >
                    <div>
                        <span className="text-xs font-bold text-slate-400 block mb-1">Fotos & Erinnerungen</span>
                        <div className="flex items-baseline gap-2">
                            <span className="text-5xl font-black text-slate-900">{stats.photo_count.toLocaleString('de-DE')}</span>
                        </div>
                        <div className="mt-3 flex gap-2">
                            <span className="bg-amber-100 text-amber-700 text-[10px] font-black px-3 py-1 rounded-full uppercase tracking-wider">
                                Rekord
                            </span>
                            <span className="text-[10px] text-slate-400 font-bold uppercase pt-1">von allen Gästen</span>
                        </div>
                    </div>
                    <div className="w-16 h-16 bg-slate-50 rounded-2xl flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-slate-200" />
                    </div>
                </motion.div>

                {/* Mini Stats Row */}
                <div className="grid grid-cols-2 gap-4">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.1 }}
                        className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100"
                    >
                        <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center mb-4">
                            <MessageSquare className="w-5 h-5 text-amber-500" />
                        </div>
                        <span className="text-2xl font-black text-slate-900 leading-none">{stats.chat_count}</span>
                        <span className="text-[10px] font-bold text-slate-400 block mt-1 uppercase">Chat-Nachrichten</span>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.2 }}
                        className="bg-white p-6 rounded-[2rem] shadow-sm border border-slate-100"
                    >
                        <div className="w-10 h-10 bg-amber-50 rounded-xl flex items-center justify-center mb-4">
                            <Sparkles className="w-5 h-5 text-amber-500" />
                        </div>
                        <span className="text-lg font-black text-slate-900 leading-none truncate block">{stats.top_player}</span>
                        <span className="text-[10px] font-bold text-slate-400 block mt-1 uppercase">Top Tänzer</span>
                    </motion.div>
                </div>

                {/* Top 3 Moments */}
                <div className="pt-10 pb-4">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <h2 className="text-2xl font-black text-slate-900">Top 3 Momente</h2>
                            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mt-1">Gewählt von euren Gästen</p>
                        </div>
                        <Star className="w-6 h-6 text-amber-400" />
                    </div>

                    <div className="space-y-6">
                        {stats.top_photos.map((photo, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.3 + (index * 0.1) }}
                                className="relative group"
                            >
                                <div className="absolute -top-3 -left-3 w-8 h-8 bg-amber-500 text-white rounded-full flex items-center justify-center font-black z-10 shadow-lg">
                                    {index + 1}
                                </div>
                                <div className="relative aspect-video rounded-[2rem] overflow-hidden shadow-xl border-4 border-white">
                                    <img src={photo.url} alt={`Top Moment ${index + 1}`} className="w-full h-full object-cover" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                                    <div className="absolute bottom-6 left-6 right-6 flex justify-between items-end">
                                        <div>
                                            <h4 className="text-white font-black text-lg">
                                                {index === 0 ? "Erster Tanz" : index === 1 ? "Tortenanschnitt" : "Brautstraußwurf"}
                                            </h4>
                                            <div className="flex items-center gap-1 text-white/70 text-[10px] font-bold uppercase">
                                                <Heart className="w-3 h-3 text-red-400 fill-red-400" /> {photo.likes} Stimmen
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>

                {/* Thanks / Video Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                    className="bg-white p-2 rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden"
                >
                    <div className="relative aspect-video rounded-[2rem] overflow-hidden mb-6">
                        <img src={video_preview || "https://images.unsplash.com/photo-1511795409834-ef04bbd61622?auto=format&fit=crop&q=80"} alt="Thanks" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                            <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center text-white shadow-2xl pulse-animation">
                                <Play className="w-8 h-8 fill-current translate-x-1" />
                            </div>
                        </div>
                        <div className="absolute top-4 left-4">
                            <span className="bg-red-500 text-[8px] font-black text-white px-2 py-0.5 rounded flex items-center gap-1 uppercase tracking-widest">
                                <span className="w-1.5 h-1.5 bg-white rounded-full animate-pulse" /> Video
                            </span>
                        </div>
                    </div>
                    <div className="px-6 pb-8 text-center italic">
                        <h3 className="text-2xl font-black text-slate-800 mb-3 not-italic">Dankeschön!</h3>
                        <p className="text-slate-500 text-sm leading-relaxed px-4">
                            "Vielen Dank für alles! Eure Anwesenheit hat diesen Tag für uns unvergesslich gemacht. Hier ist eine kleine Videobotschaft von uns beiden."
                        </p>
                    </div>
                </motion.div>

                {/* AI Story Section (New Addition for variety) */}
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.7 }}
                    className="bg-amber-100/30 p-8 rounded-[2rem] border border-amber-200/50"
                >
                    <div className="flex items-center gap-2 mb-4">
                        <Sparkles className="w-5 h-5 text-amber-600" />
                        <span className="text-[10px] font-black text-amber-600 uppercase tracking-widest">Unsere Geschichte</span>
                    </div>
                    <p className="text-slate-700 font-serif text-lg leading-relaxed italic">
                        {text}
                    </p>
                </motion.div>
            </div>

            {/* Sticky Bottom UI */}
            <div className="fixed bottom-6 left-6 right-6 z-50 flex gap-3">
                <button
                    onClick={onShare}
                    className="flex-1 bg-amber-500 hover:bg-amber-600 text-white rounded-2xl py-5 font-black uppercase tracking-widest text-xs shadow-xl shadow-amber-200 flex items-center justify-center gap-2 transition-all active:scale-95"
                >
                    <Share2 className="w-4 h-4" /> Recap teilen
                </button>
                <button
                    onClick={onDownload}
                    className="w-16 h-16 bg-white text-slate-900 rounded-2xl border border-slate-200 shadow-xl flex items-center justify-center transition-all active:scale-95"
                >
                    <Download className="w-6 h-6" />
                </button>
            </div>
        </div>
    );
};

export default EventRecapView;
